import * as React from "react";
import { Link } from "react-router";
import { Check } from "lucide-react";

import { Button } from "@/components/ui/button";
import { CircleLoader } from "@/components/ui/circleLoader";
import { useGetBusinessReportsQuery } from "@/modules/snapshots/businessReports.api";
import type { BusinessReport } from "@/modules/snapshots/businessReports.types";
import type { TemplateRunReport as TemplateRunReportData } from "../documentAuthoring.types";
import type { CheckStatus } from "../hooks/useAuthoringRun";
import { plural } from "./helpers";
import { TemplateRunReport, type TemplateCheck } from "./TemplateRunReport";

/** The shared template library page where templates are created and edited. */
export const TEMPLATE_LIBRARY_PATH = "/snapshots/templates";

interface Props {
  aiAuthor: string;
  busy: boolean;
  running: boolean;
  editorReady: boolean;
  error: string | null;
  statuses: CheckStatus[];
  report: TemplateRunReportData | null;
  onRun: (templateId: string, checkCount: number) => void;
}

const emptyList: BusinessReport[] = [];

/** Every prompt of a library template is one check: the prompt name is the heading. */
const checksOf = (template: BusinessReport): TemplateCheck[] =>
  [...template.prompts]
    .sort((first, second) => first.order - second.order)
    .map(prompt => ({ heading: prompt.name, prompt: prompt.prompt }));

/** Authoring templates first (the purpose-built ones), then SnapShot and report templates by name. */
const sortForPicker = (templates: BusinessReport[]): BusinessReport[] =>
  [...templates]
    .filter(template => template.is_active)
    .sort((first, second) => {
      const firstAuthoring = first.type === "authoring_template" ? 0 : 1;
      const secondAuthoring = second.type === "authoring_template" ? 0 : 1;
      return firstAuthoring - secondAuthoring || first.name.localeCompare(second.name);
    });

export const TemplateTab: React.FC<Props> = ({
  aiAuthor,
  busy,
  running,
  editorReady,
  error,
  statuses,
  report,
  onRun,
}) => {
  const { data: allTemplates = emptyList, isLoading } = useGetBusinessReportsQuery();
  const templates = React.useMemo(() => sortForPicker(allTemplates), [allTemplates]);
  const [templateId, setTemplateId] = React.useState("");
  const template = templates.find(item => item.id === templateId);
  const checks = React.useMemo(() => (template ? checksOf(template) : []), [template]);
  const totalApplied = report ? report.results.reduce((sum, result) => sum + result.applied.length, 0) : 0;
  const failedChecks = report ? report.results.filter(result => result.error).length : 0;

  return (
    <section className="space-y-3 px-4 py-3">
      <div className="flex items-center justify-between">
        <span className="text-xxs font-semibold uppercase tracking-wide text-gray-500">Template</span>
        <Link to={TEMPLATE_LIBRARY_PATH} className="text-xs text-coral underline-offset-2 hover:underline">
          Manage templates
        </Link>
      </div>
      {error && (
        <p className="rounded-md border border-danger/40 bg-danger/5 px-3 py-2 text-xs text-danger" role="alert">
          {error}
        </p>
      )}

      {templates.length === 0 && !isLoading ? (
        <p className="text-xs text-gray-600">
          No templates yet. Create an <strong>Authoring Template</strong> in the{" "}
          <Link to={TEMPLATE_LIBRARY_PATH} className="text-coral underline">
            template library
          </Link>
          : each prompt is one check {aiAuthor} runs over the document.
        </p>
      ) : (
        <select
          aria-label="Template"
          className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
          value={templateId}
          disabled={busy}
          onChange={event => setTemplateId(event.target.value)}
        >
          <option value="">Choose a template…</option>
          {templates.map(item => (
            <option key={item.id} value={item.id}>
              {item.name} ({plural(item.prompts.length, "check", "checks")})
              {item.type === "authoring_template" ? "" : item.type === "business_report" ? " · Report" : " · Snapshot"}
              {item.visibility === "private" ? " · Private" : ""}
            </option>
          ))}
        </select>
      )}

      {template && (
        <>
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-ui-heading-text" title={template.name}>
                {template.name}
              </p>
              <p className="text-xxs text-gray-500">{plural(checks.length, "check", "checks")}</p>
            </div>
          </div>
          <Button
            type="button"
            variant="coral"
            className="w-full"
            onClick={() => onRun(template.id, checks.length)}
            disabled={!editorReady || busy || checks.length === 0}
          >
            {running ? (
              <>
                <CircleLoader /> Running…
              </>
            ) : report ? (
              "Run template again"
            ) : (
              "Run template"
            )}
          </Button>
          {report && (
            <p className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800">
              <Check className="h-4 w-4 shrink-0" aria-hidden="true" />
              {plural(totalApplied, "edit", "edits")} added across {plural(report.results.length, "check", "checks")} by{" "}
              {report.author}
            </p>
          )}
          {failedChecks > 0 && (
            <p className="text-xs text-danger">
              {plural(failedChecks, "check couldn't", "checks couldn't")} run. See below, then run the template again to
              retry.
            </p>
          )}
          <TemplateRunReport checks={checks} statuses={statuses} results={report?.results ?? null} />
        </>
      )}
    </section>
  );
};
